import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Tile } from "./waShared";

// ─── Status + Test Connection + Send Test Text ───────────────────────
function StatusPanel() {
  const [status, setStatus] = useState(null);
  const [conn, setConn] = useState(null);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("🟢 رسالة اختبار من نظام ثواني — التكامل يعمل بنجاح.");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, d] = await Promise.all([
        api.get("/whatsapp/status"),
        api.get("/whatsapp/test-connection"),
        api.get("/whatsapp/weekly-digest").catch(() => ({ data: null })),
      ]);
      setStatus(s.data);
      setConn(c.data);
      setDigest(d.data);
    } catch (e) {
      toast.error("تعذّر جلب حالة WhatsApp");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    if (!phone.trim() || !message.trim()) { toast.error("الرقم والرسالة مطلوبان"); return; }
    setSending(true);
    try {
      const r = await api.post("/whatsapp/send-test", { to: phone.trim(), message });
      toast.success(`تم الإرسال إلى ${r.data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر الإرسال");
    } finally { setSending(false); }
  };

  if (loading) return <div className="card" style={{ padding: 20, textAlign: "center" }}>⏳ جارٍ التحميل...</div>;

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 12 }} data-testid="wa-status-card">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
          <Tile label="حالة التهيئة" value={status?.configured ? "✅ مهيّأ" : "❌ غير مهيّأ"} />
          <Tile label="الاسم المُعتمد" value={conn?.verified_name || "—"} />
          <Tile label="رقم WhatsApp" value={conn?.display_phone_number || "—"} />
          <Tile label="تصنيف الجودة" value={conn?.quality_rating || "—"} />
        </div>
        {!conn?.connected && (
          <div style={{ marginTop: 10, padding: 10, background: "#fee2e2", borderRadius: 8, color: "#991b1b", fontSize: 12.5 }}>
            ⚠ تعذّر الاتصال بـ Meta: {conn?.reason || "—"}
          </div>
        )}
        {status?.missing_keys?.length > 0 && (
          <div style={{ marginTop: 10, padding: 10, background: "#fef3c7", borderRadius: 8, color: "#92400e", fontSize: 12 }}>
            ⚠ مفاتيح مفقودة في الـenv: {status.missing_keys.join(", ")}
          </div>
        )}
      </div>

      {/* Weekly digest tile (v119.56) */}
      {digest && (
        <div className="card" style={{ padding: 14, marginBottom: 12, background: "linear-gradient(180deg, #fff 0%, #fef3c7 100%)" }} data-testid="wa-digest">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-deep)" }}>
              📊 تقرير الأسبوع الذكي ({digest.period?.from} → {digest.period?.to})
            </div>
            <span style={{ fontSize: 10.5, color: "var(--muted)" }}>يُحدّث تلقائياً</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
            <Tile label="رسائل واردة" value={digest.totals?.inbound_messages} />
            <Tile label="رسائل صادرة" value={digest.totals?.outbound_messages} />
            <Tile label="حملات" value={digest.totals?.campaigns_sent} />
            <Tile label="شكاوى" value={digest.totals?.complaints} />
            <Tile label="إشادات" value={digest.totals?.praises} />
            <Tile label="استفسارات" value={digest.totals?.questions} />
            <Tile label="عاجلة (high+critical)" value={digest.totals?.high_urgency} />
            <Tile label="ساعة الذروة" value={digest.peak_hour != null ? `${digest.peak_hour}:00` : "—"} />
          </div>
          {(digest.top_topics || []).length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12 }}>
              <b>🔥 أكثر المواضيع تكراراً:</b>
              <ul style={{ marginInlineStart: 20, lineHeight: 1.8 }}>
                {digest.top_topics.map((t, i) => (
                  <li key={i}><b>{t.topic}</b> · {t.count} مرة</li>
                ))}
              </ul>
            </div>
          )}
          {(digest.complaint_samples || []).length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#991b1b" }}>
                ⚠ عيّنات شكاوى الأسبوع ({digest.complaint_samples.length})
              </summary>
              <ul style={{ marginTop: 6, paddingInlineStart: 18, fontSize: 11.5, lineHeight: 1.7 }}>
                {digest.complaint_samples.map((c, i) => (
                  <li key={i}><b>{c.from}</b> — {c.topic || c.summary} <span style={{ color: c.urgency === "critical" ? "#7c2d12" : "#dc2626", fontSize: 10 }}>· {c.urgency}</span></li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <div className="card" style={{ padding: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: "var(--brown-deep)" }}>
          🧪 إرسال رسالة اختبار (داخل نافذة 24 ساعة)
        </div>
        <p style={{ fontSize: 11.5, color: "var(--muted)", marginBottom: 12 }}>
          ⚠ ملاحظة: لإرسال نص حر، يجب أن يكون الرقم قد راسلك خلال آخر 24 ساعة. خارج هذه النافذة، استخدم تبويب «إرسال قالب».
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10, marginBottom: 10 }}>
          <input className="sim-input" type="tel" placeholder="05XXXXXXXX أو +9665XXXXXXXX"
            value={phone} onChange={(e) => setPhone(e.target.value)}
            data-testid="wa-test-phone" />
          <input className="sim-input" type="text"
            value={message} onChange={(e) => setMessage(e.target.value)}
            data-testid="wa-test-message" />
        </div>
        <button type="button" onClick={sendTest} disabled={sending}
          className="btn btn-dark" style={{ fontSize: 13 }}
          data-testid="wa-send-test-btn">
          {sending ? "⏳ يتم الإرسال..." : "🚀 إرسال الرسالة"}
        </button>
      </div>
    </div>
  );
}

export default StatusPanel;
